import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RxIntuitiveTeethDiagramViewService } from '@modules/rx-intuitive-teeth-orchestrator/services/teeth-diagram/rx-intuitive-teeth-diagram-view.service';
import { RxIntuitiveTeethDiagramMutationService } from '@modules/rx-intuitive-teeth-orchestrator/services/teeth-diagram/rx-intuitive-teeth-diagram-mutation.service';
import { RxIntuitiveTeethDiagramQuery } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-query';
import { ArtToothForDiagram, ArtTreatmentIntegrity, ArtTreatmentDefinition, ArtToothTreatment, ArtTreatmentListItem } from '../models';
import { ArtTreatmentQuery } from '../state/art-treatment.query';
import { ArtTreatmentStore } from '../state/art-treatment.store';
import {
	ART_PRESET_TOOTH_IDS,
	isArtPresetBaseline,
	isArtPresetTooth,
	isArtTreatmentUnassigned
} from '../constants/art-treatment.constants';
import { ArtTreatmentRulesService } from './art-treatment-rules.service';
import { IntuitiveIndication, IntuitiveToothForDiagram } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { RxIntuitiveTeethImagesService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-teeth-images.service';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { ArchAlignmentQuery } from '@modules/rx-intuitive-arch-alignment/arch-alignment.query';
import type { Tooth } from '@modules/teeth-diagram/models/tooth';

@Injectable()
export class ArtTreatmentFacade {
	readonly upperJaw$: Observable<ArtToothForDiagram[]> = this.mapJaw(this.diagramViewService.upperJaw$);
	readonly lowerJaw$: Observable<ArtToothForDiagram[]> = this.mapJaw(this.diagramViewService.lowerJaw$);
	readonly selectedToothIds$ = this.diagramViewService.selectedToothIds$;
	readonly hasSelectedTeeth$ = this.diagramViewService.selectedToothIds$.pipe(map(ids => ids.length > 0));
	readonly isTeethDiagramReadOnly$ = this.diagramViewService.isTeethDiagramReadOnly$;

	readonly availableIntegrity$ = of(this.rulesService.getAvailableIntegrity());
	readonly availableDefinitions$ = of(this.rulesService.getAvailableDefinitions());

	readonly enabledIntegrity$ = combineLatest([this.selectedToothIds$, this.upperJaw$, this.lowerJaw$]).pipe(
		map(([selectedToothIds, upperJaw, lowerJaw]) =>
			this.rulesService.getEnabledIntegrity(this.getSelectedTeeth(selectedToothIds, upperJaw, lowerJaw))
		)
	);
	readonly enabledDefinitions$ = combineLatest([
		this.selectedToothIds$,
		this.upperJaw$,
		this.lowerJaw$,
		this.archAlignmentQuery.onUpperJaw$,
		this.archAlignmentQuery.onLowerJaw$
	]).pipe(
		map(([selectedToothIds, upperJaw, lowerJaw, isUpperJawEnabled, isLowerJawEnabled]) => {
			const selectedTeeth = this.getSelectedTeeth(selectedToothIds, upperJaw, lowerJaw);
			const disabledJawToothIds = new Set<number>();

			if (!isUpperJawEnabled) {
				upperJaw.forEach(tooth => disabledJawToothIds.add(tooth.toothId));
			}

			if (!isLowerJawEnabled) {
				lowerJaw.forEach(tooth => disabledJawToothIds.add(tooth.toothId));
			}

			return this.rulesService.getEnabledDefinitions(selectedTeeth, disabledJawToothIds);
		})
	);

	readonly selectedIntegrity$: Observable<ArtTreatmentIntegrity | undefined> = combineLatest([
		this.selectedToothIds$,
		this.teethQuery.teethFlat$
	]).pipe(
		map(([selectedToothIds, teeth]) => {
			if (selectedToothIds.length === 0) {
				return undefined;
			}

			const treatments = this.getTreatmentsByToothId(teeth);
			const integrities = selectedToothIds.map(id => treatments[id]?.integrity ?? ArtTreatmentIntegrity.Adjacent);

			return integrities.every(integrity => integrity === integrities[0]) ? integrities[0] : undefined;
		})
	);

	readonly selectedDefinitions$: Observable<ArtTreatmentDefinition[]> = combineLatest([
		this.selectedToothIds$,
		this.teethQuery.teethFlat$
	]).pipe(
		map(([selectedToothIds, teeth]) => {
			if (selectedToothIds.length === 0) {
				return [];
			}

			const selectedTeeth = this.getSelectedTeethFromState(selectedToothIds, this.getTreatmentsByToothId(teeth));

			return this.rulesService.getSelectedDefinitions(selectedTeeth);
		})
	);

	readonly treatmentListItems$: Observable<ArtTreatmentListItem[]> = combineLatest([
		this.diagramViewService.upperJaw$,
		this.diagramViewService.lowerJaw$,
		this.teethQuery.teethFlat$,
		this.artQuery.lastTouchedAt$
	]).pipe(
		map(([upperJaw, lowerJaw, teeth, lastTouchedAt]) => {
			const treatments = this.getTreatmentsByToothId(teeth);

			return [...upperJaw, ...lowerJaw]
				.map(tooth => {
					const treatment = treatments[tooth.toothId];

					return treatment == null
						? null
						: {
								toothId: tooth.toothId,
								toothNumberInSystem: tooth.toothNumberInSystem,
								treatment,
								lastTouchedAt: lastTouchedAt[tooth.toothId] ?? 0,
								canReset: this.canResetTreatment(tooth.toothId, treatment)
							};
				})
				.filter((item): item is ArtTreatmentListItem => item != null)
				.filter(item => !isArtTreatmentUnassigned(item.toothId, item.treatment.integrity, item.treatment.definitions))
				.sort((a, b) => b.lastTouchedAt - a.lastTouchedAt || a.toothId - b.toothId);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	assignIntegrityToSelectedTeeth(integrity: ArtTreatmentIntegrity): void {
		const selectedTeeth = this.getSelectedTeethFromState();
		const treatments = this.rulesService.getTreatmentsAfterIntegrityChange(selectedTeeth, integrity);

		this.updateCanonicalTeeth(treatments);
		this.artStore.touchTeeth(Object.keys(treatments).map(Number));
	}

	assignDefinitionToSelectedTeeth(definition: ArtTreatmentDefinition): void {
		const selectedTeeth = this.getSelectedTeethFromState();
		const treatments = this.rulesService.getTreatmentsAfterDefinitionToggle(selectedTeeth, definition);

		this.updateCanonicalTeeth(treatments);
		this.artStore.touchTeeth(Object.keys(treatments).map(Number));
	}

	resetTreatment(toothId: number): void {
		this.artStore.resetTreatment(toothId);
		this.diagramStore.updateTeethByIds([toothId], {
			ArtTreatment: this.isPresetTooth(toothId) ? this.toDto(this.getPresetTreatment()) : null
		});
	}

	clearToothSelection(): void {
		this.diagramMutationService.clearToothSelection();
	}

	toggleToothSelection(toothId: number): void {
		if (!this.diagramQuery.selectedToothIds.includes(toothId) && this.hasAssignedTreatmentToSelectedTeeth()) {
			this.diagramMutationService.clearToothSelection();
		}

		this.diagramMutationService.toggleToothSelection(toothId);
	}

	constructor(
		private readonly diagramViewService: RxIntuitiveTeethDiagramViewService,
		private readonly diagramMutationService: RxIntuitiveTeethDiagramMutationService,
		private readonly diagramQuery: RxIntuitiveTeethDiagramQuery,
		private readonly artQuery: ArtTreatmentQuery,
		private readonly artStore: ArtTreatmentStore,
		private readonly rulesService: ArtTreatmentRulesService,
		private readonly imagesService: RxIntuitiveTeethImagesService,
		private readonly diagramStore: TeethDiagramStore,
		private readonly teethQuery: TeethDiagramQuery,
		private readonly archAlignmentQuery: ArchAlignmentQuery
	) {}

	initialize(): void {
		this.initializePresetTreatments();
	}

	private canResetTreatment(toothId: number, treatment: ArtToothTreatment): boolean {
		return !this.isPresetBaseline(toothId, treatment);
	}

	private isPresetBaseline(toothId: number, treatment: ArtToothTreatment): boolean {
		return isArtPresetBaseline(toothId, treatment.integrity, treatment.definitions);
	}

	private getSelectedTeethFromState(
		selectedToothIds: number[] = this.diagramQuery.selectedToothIds,
		treatments: Readonly<Record<number, ArtToothTreatment>> = this.getCurrentTreatments()
	): ArtToothForDiagram[] {
		return selectedToothIds.map(
			toothId =>
				({
					toothId,
					artTreatment: treatments[toothId] ?? this.getDefaultTreatment(toothId)
				}) as ArtToothForDiagram
		);
	}

	private getSelectedTeeth(
		selectedToothIds: number[],
		upperJaw: ArtToothForDiagram[],
		lowerJaw: ArtToothForDiagram[]
	): ArtToothForDiagram[] {
		const teeth = [...upperJaw, ...lowerJaw];

		return selectedToothIds
			.map(toothId => teeth.find(tooth => tooth.toothId === toothId))
			.filter((tooth): tooth is ArtToothForDiagram => tooth != null);
	}

	private getCurrentTreatments(): Record<number, ArtToothTreatment> {
		return this.getTreatmentsByToothId(this.teethQuery.teethFlat);
	}

	private hasAssignedTreatmentToSelectedTeeth(): boolean {
		const treatments = this.getCurrentTreatments();

		return this.diagramQuery.selectedToothIds.some(toothId => {
			const treatment = treatments[toothId];

			return treatment != null && this.hasAssignedTreatment(toothId, treatment);
		});
	}

	private hasAssignedTreatment(toothId: number, treatment: ArtToothTreatment): boolean {
		return !isArtTreatmentUnassigned(toothId, treatment.integrity, treatment.definitions);
	}

	private getTreatmentsByToothId(teeth: readonly Tooth[]): Record<number, ArtToothTreatment> {
		return teeth.reduce<Record<number, ArtToothTreatment>>((treatments, tooth) => {
			if (tooth.ToothID != null && tooth.ArtTreatment != null) {
				treatments[tooth.ToothID] = {
					integrity: tooth.ArtTreatment.Integrity as ArtTreatmentIntegrity,
					definitions: [...tooth.ArtTreatment.Definitions]
				};
			}

			return treatments;
		}, {});
	}

	private updateCanonicalTeeth(treatments: Readonly<Record<number, ArtToothTreatment>>): void {
		for (const [toothId, treatment] of Object.entries(treatments)) {
			this.diagramStore.updateTeethByIds([Number(toothId)], { ArtTreatment: this.toDto(treatment) });
		}
	}

	private initializePresetTreatments(): void {
		const currentTreatments = this.getCurrentTreatments();

		for (const toothId of ART_PRESET_TOOTH_IDS) {
			if (currentTreatments[toothId] == null) {
				this.diagramStore.updateTeethByIds([toothId], { ArtTreatment: this.toDto(this.getPresetTreatment()) });
			}
		}
	}

	private isPresetTooth(toothId: number): boolean {
		return isArtPresetTooth(toothId);
	}

	private getPresetTreatment(): ArtToothTreatment {
		return {
			integrity: ArtTreatmentIntegrity.Adjacent,
			definitions: [ArtTreatmentDefinition.Unmovable]
		};
	}

	private getDefaultTreatment(toothId: number): ArtToothTreatment {
		return this.isPresetTooth(toothId) ? this.getPresetTreatment() : { integrity: ArtTreatmentIntegrity.Adjacent, definitions: [] };
	}

	private toDto(treatment: ArtToothTreatment): Tooth['ArtTreatment'] {
		return {
			Integrity: treatment.integrity,
			Definitions: [...treatment.definitions]
		};
	}

	private mapJaw(jaw$: Observable<IntuitiveToothForDiagram[]>): Observable<ArtToothForDiagram[]> {
		return combineLatest([jaw$, this.teethQuery.teethFlat$]).pipe(
			map(([jaw, teeth]) => {
				const treatments = this.getTreatmentsByToothId(teeth);

				return jaw.map(tooth => {
					const artTreatment = treatments[tooth.toothId] ?? this.getDefaultTreatment(tooth.toothId);

					return {
						...tooth,
						svgSymbolId: [ArtTreatmentIntegrity.Missing, ArtTreatmentIntegrity.MissingRestorationPlanned].includes(
							artTreatment.integrity
						)
							? this.imagesService.getSymbolId(tooth.toothId, IntuitiveIndication.Missing_edentulousSpace)
							: tooth.svgSymbolId,
						artTreatment
					};
				});
			}),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}
}
